import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles, withStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import FilterListIcon from '@material-ui/icons/FilterList';
import ListIcon from '@material-ui/icons/List';
import Grid from '@material-ui/core/Grid';
import SearchInput from 'eventtia-ui-components/lib/SearchInput';
import SelectButton from 'eventtia-ui-components/lib/SelectButton';
import Button from 'eventtia-ui-components/lib/Button';
import loadable from '@loadable/component';
import Loader from 'eventtia-ui-components/lib/Loader';
import GridViewIcon from '../../assets/GridViewIcon';
import { fetchConfig } from '../../actions/callApi';
import { setFilters } from '../../actions/filters';
import CustomPropTypes from '../../helpers/CustomPropTypes';

const FilterLoader = withStyles({
  loader: {
    height: 150,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})(({ classes }) => <Loader className={classes.loader} loading />);

const FetchingLoader = withStyles({
  loader: {
    height: 32,
    display: 'flex',
    alignItems: 'center',
  },
})(({ classes }) => <Loader className={classes.loader} loading />);

// eslint-disable-next-line prefer-template
const AsyncFilterContent = loadable((props) => import('../filters/' + props.component), {
  fallback: <FilterLoader />,
});

const useStyles = makeStyles((theme) => ({
  mainActionsWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    width: '100%',
    flexWrap: 'wrap',
    margin: theme.spacing(3, 0, 1.5, 0),
  },
  viewButton: {
    height: 36,
    marginRight: theme.spacing(2),
    borderRadius: 50,
  },
  search: {
    display: 'flex',
    alignItems: 'center',
  },
  filters: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-end',
    color: theme.palette.darkGrey.light,
    marginBottom: theme.spacing(4),
  },
  filterSmallButton: {
    marginRight: theme.spacing(1),
  },
  filterButton: {
    height: 36,
    color: theme.palette.primary.main,
    borderRadius: 50,
  },
  submitButton: {
    display: 'inline-block',
    maxWidth: '100%',
    minWidth: 'unset',
    color: theme.palette.primary.main,
    padding: theme.spacing(0, 2),
    borderRadius: 12,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  filterContent: {
    width: 460,
    height: 300,
    maxWidth: '100%',
  },
  searchInput: {
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },
}));

export const Component = ({
  entity, activeFilters, fetchParams, fetchStatus, setListView, listView,
  setFilters: dispatchSetFilters, sort, setSort, entities, order, meta,
}) => {
  const classes = useStyles();
  const { t } = useTranslation(['global, attendees']);
  const searchRef = useRef(null);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentValues, setCurrentValues] = useState({});
  const toggleFilters = useCallback(() => setFiltersOpen(!filtersOpen),
    [filtersOpen, setFiltersOpen]);

  const { filters, sortOptions } = fetchConfig[entity];
  const barFilters = useMemo(() => {
    const result = {};
    Object.keys(filters).forEach((filter) => {
      if (filters[filter].type) result[filter] = filters[filter];
    });
    return result;
  }, [filters]);

  const filtersEmpty = useMemo(() => {
    let result = true;
    Object.keys(barFilters).forEach((filter) => {
      const { isEmpty } = barFilters[filter];
      if (!isEmpty) result = false;
      else result &&= isEmpty(entities, order, meta);
    });
    return result;
  }, [barFilters, entities, order, meta]);

  const search = useCallback(() => {
    dispatchSetFilters(entity, { search: searchRef.current?.value }, fetchParams);
  }, [dispatchSetFilters, entity, fetchParams]);

  const clearSearch = useCallback(() => {
    if (activeFilters.search) dispatchSetFilters(entity, { search: undefined }, fetchParams);
    else if (searchRef.current) {
      searchRef.current.value = '';
      searchRef.current.blur();
    }
  }, [dispatchSetFilters, entity, fetchParams, activeFilters]);

  const onClearFilters = useCallback((dispatch = true) => {
    const { search: _, ...otherFilters } = activeFilters;
    if (otherFilters) {
      const cleanedFilters = {};
      Object.keys(otherFilters).forEach((currentFilter) => {
        if (typeof otherFilters[currentFilter] === 'string') cleanedFilters[currentFilter] = '';
        else if (Array.isArray(otherFilters[currentFilter])) cleanedFilters[currentFilter] = [];
        else if (typeof otherFilters[currentFilter] === 'object') {
          const subfilters = otherFilters[currentFilter];
          cleanedFilters[currentFilter] = {};
          Object.keys(subfilters).forEach((subfilter) => {
            if (typeof subfilters[subfilter] === 'string') cleanedFilters[currentFilter][subfilter] = '';
            else if (Array
              .isArray(subfilters[subfilter])) cleanedFilters[currentFilter][subfilter] = [];
          });
        }
      });
      setCurrentValues({ search: _, ...cleanedFilters });
      if (dispatch) dispatchSetFilters(entity, cleanedFilters, fetchParams);
    }
  }, [dispatchSetFilters, activeFilters, fetchParams, entity]);

  const sortItems = useMemo(() => {
    if (!sortOptions) return undefined;
    return sortOptions.map((option) => ({
      label: t(`global:filters.sort.${option}`),
      onClick: () => {
        setSort(option);
      },
    }));
  }, [sortOptions, setSort, t]);

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.value = activeFilters.search || '';
      if (!activeFilters.search) searchRef.current.blur();
    }
  }, [activeFilters.search]);

  useEffect(() => {
    setCurrentValues({
      ...currentValues,
      ...activeFilters,
    });
  }, [activeFilters]);

  const onChange = useCallback((key) => (value) => {
    setCurrentValues({
      ...currentValues,
      [key]: value,
    });
  }, [setCurrentValues, currentValues]);

  const loading = fetchStatus[entity].isFetchingFirstPage;

  const onSubmit = useCallback(() => {
    dispatchSetFilters(entity, currentValues, fetchParams);
  }, [dispatchSetFilters, currentValues, entity, fetchParams]);

  return (
    <>
      <div className={classes.mainActionsWrapper}>
        {setListView && (
        <Button
          className={classes.viewButton}
          icon={!listView ? <ListIcon /> : <GridViewIcon />}
          variant="tertiary"
          onClick={() => setListView(!listView)}
          small
        >
          {!listView ? t('attendees:actions.listView') : t('attendees:actions.gridView')}
        </Button>
        )}
        <div className={classes.search}>
          {!!sortItems && (
            <SelectButton
              label={sort ? t(`global:filters.sort.${sort}`) : t('global:filters.sort.sortBy')}
              items={sortItems}
              small
            />
          )}
          {!filtersEmpty && mobile ? (
            <IconButton
              aria-label="filter"
              className={classes.filterSmallButton}
              onClick={toggleFilters}
              color="primary"
              size="small"
            >
              <FilterListIcon />
            </IconButton>
          ) : (
            <>
              {!filtersEmpty && (
                <Button
                  className={classes.filterButton}
                  icon={<FilterListIcon />}
                  variant="tertiary"
                  onClick={toggleFilters}
                  small
                >
                  {filtersOpen ? t('global:actions.hideFilters') : t('global:filters.title')}
                </Button>
              )}
            </>
          )}
          {!!filters.search && (
            <SearchInput
              placeholder={t('global:forms.searchKeyword')}
              defaultValue={activeFilters.search || ''}
              inputRef={searchRef}
              onSubmit={search}
              onClear={clearSearch}
              className={classes.searchInput}
              fullWidth={false}
              margin="dense"
            />
          )}
        </div>
      </div>
      {filtersOpen && (
        <Grid container spacing={2} className={classes.filters}>
          {barFilters && Object.keys(barFilters).map((filter) => (
            <AsyncFilterContent
              key={filter}
              value={currentValues[filter]}
              onChange={onChange(filter)}
              component={barFilters[filter].type}
            />
          ))}
          <Grid item xs={12} md={6} lg={4}>
            <Button
              variant="secondary"
              onClick={onSubmit}
              className={classes.submitButton}
              small
            >
              {t('global:actions.search')}
            </Button>
            <Button
              variant="tertiary"
              onClick={onClearFilters}
              className={classes.submitButton}
              small
            >
              {t('global:actions.clear')}
            </Button>
          </Grid>
          {loading && (
            <Grid item xs={12} md={6} lg={2}>
              <FetchingLoader />
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

Component.propTypes = {
  entity: PropTypes.string.isRequired,
  activeFilters: PropTypes.objectOf(CustomPropTypes.filter).isRequired,
  fetchParams: PropTypes.objectOf(CustomPropTypes.filter),
  setFilters: PropTypes.func.isRequired,
  sort: PropTypes.string.isRequired,
  setSort: PropTypes.func.isRequired,
  entities: CustomPropTypes.entities.isRequired,
  order: PropTypes.arrayOf(PropTypes.string),
  meta: PropTypes.shape({
    meetingStatuses: PropTypes.objectOf(
      PropTypes.number
    ),
  }).isRequired,
  fetchStatus: CustomPropTypes.fetchStatus.isRequired,
  setListView: PropTypes.func,
  listView: PropTypes.bool,
};

Component.defaultProps = {
  fetchParams: undefined,
  order: undefined,
  setListView: undefined,
  listView: false,
};

const mapStateToProps = ({
  filters,
  entities,
  fetchStatus,
  fetchStatus: {
    attendeeTypes: {
      order,
    },
  },
  meta,
}, { entity }) => ({
  activeFilters: filters[entity],
  entities,
  fetchStatus,
  order,
  meta,
});

export default connect(mapStateToProps, { setFilters })(Component);
